import { useDispatch, useSelector } from 'react-redux';
import './css/app.css';
import './css/main.css';
import './css/common.css';
import './css/payment.css';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { change_Current_Location, changeHeaderTitle, setMyData } from '../store';
import axios from 'axios';
import { LoadingModal, MessageModal } from './component/modal/modal';
import { Search, X } from 'lucide-react';
import { changeNum } from './common/function';
import { BRAND_LOGO, HAS_CUSTOM_LOGO } from '../config/brand';

function UrlComplete() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const hostLogoImg = useSelector((state) => state.hostLogoImg.value);
    const MyData = useSelector((state) => state.MyData);

    const [message, setMessage] = useState({ msg: "" });
    const receiptRef = useRef(null);

    // -------- Route Guard: state 없이 접근 시 차단 --------
    const hasState = !!location.state && !!location.state.receipt; // receipt 필수
    useEffect(() => {
        if (!hasState) {
            navigate("/", { replace: true }); // 경로는 프로젝트에 맞게
            return;
        }
        dispatch(change_Current_Location("URL상품결제"));
        dispatch(changeHeaderTitle({ mainTitle: "URL상품결제", subTitle: "" }));
    }, [hasState, navigate, dispatch]);

    if (!hasState) return null; // 리다이렉트 중엔 렌더링하지 않음

    // -------- 받은 데이터 --------
    const raw = location.state.receipt; // urlPayment에서 navigate로 넘긴 tmp
    const listData = location.state.listData; // 필요 시 사용

    // -------- 표시용 유틸 --------
    const stripDigits = (v) => (v ?? "").toString().replace(/\D/g, "");
    const formatKRW = (v) => {
        const raw = stripDigits(v);
        if (!raw) return "0원";
        return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "원";
    };

    const formatCompactToKorean = (ts) => {
        if (ts == null) return "";
        const s = String(ts).replace(/\D/g, ""); // 숫자만 추출
        if (s.length !== 14) return String(ts);  // 기대 포맷이 아니면 원본 반환

        const y = s.slice(0, 4);
        const M = s.slice(4, 6);
        const d = s.slice(6, 8);
        const hh = s.slice(8, 10);
        const mm = s.slice(10, 12);
        const ss = s.slice(12, 14);

        return `${y}년 ${M}월 ${d}일 ${hh}시${mm}분${ss}초`;
    }

    // 백엔드 응답 키가 다른 경우 대비
    const receipt = {
        title: "결제내역",
        method: "신용카드",
        tid: raw.tid,
        date: formatCompactToKorean(raw.appDtm),
        amount: Number(raw.amt),
        storeName: MyData.co_nm || "",
        productName: raw.goodsName,
        buyerName: raw.ordNm,
        phone: "",
    };

    // -------- 반응형 로고 --------
    const ResponsiveImage = ({ smallSrc, largeSrc, alt, breakpoint = 1280 }) => {
        const [windowWidth, setWindowWidth] = useState(window.innerWidth);
        useEffect(() => {
            const onResize = () => setWindowWidth(window.innerWidth);
            window.addEventListener("resize", onResize);
            return () => window.removeEventListener("resize", onResize);
        }, []);
        const imageSrc = windowWidth <= breakpoint ? smallSrc : largeSrc;
        return <img src={imageSrc} alt={alt} className={windowWidth <= breakpoint ? "w-8" : "w-32"} />;
    };

    // -------- 이미지 저장 --------
    const handleDownloadImage = async () => {
        try {
            const el = receiptRef.current;
            if (!el) return;
            const { default: html2canvas } = await import("html2canvas");
            const canvas = await html2canvas(el, {
                backgroundColor: "#ffffff",
                scale: 2,
                useCORS: true,
                logging: false,
                windowWidth: el.scrollWidth,
                windowHeight: el.scrollHeight,
            });
            const dataUrl = canvas.toDataURL("image/png");
            const a = document.createElement("a");
            const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.href = dataUrl;
            a.download = `결제내역_${ymd}.png`;
            a.click();
        } catch (err) {
            console.error(err);
            setMessage({ msg: "이미지 저장 중 오류가 발생했어요." });
        }
    };

    return (
        <div className="content" id="contents" style={{ width: "100%", padding: "0" }}>
            <MessageModal message={message} />

            {/* 상단 로고 (모바일) */}
            <div className={`mobile-menu md:hidden`}>
                <div className="mobile-menu-bar">
                    <Link to={`/`} className="main_logo flex mr-auto gnb-link">
                        {HAS_CUSTOM_LOGO || hostLogoImg === "onu" ? (
                            <ResponsiveImage smallSrc={BRAND_LOGO} largeSrc={BRAND_LOGO} alt="Service Logo" breakpoint={768} />
                        ) : (
                            <ResponsiveImage smallSrc="/img/dl_symbol.png" largeSrc="/img/dl_logo_white.png" alt="East Commerce Logo" breakpoint={1280} />
                        )}
                    </Link>
                </div>
            </div>

            {/* 상단 바 */}
            <div
                className="top-bar-boxed h-[70px] md:h-[65px] z-[51] border-b border-white/[0.08] mt-12 md:mt-0 -mx-3 sm:-mx-8 md:-mx-0 px-3 md:border-b-0 relative md:fixed md:inset-x-0 md:top-0 md:px-8 md:bg-gradient-to-b md:from-slate-100 md:to-transparent dark:md:from-darkmode-700"
                style={{ maxWidth: "1920px", margin: "0 auto" }}
            >
                <div className="h-full flex items-center -intro-x h-[45px]" style={{ float: "left" }}>
                    <Link to={`/paylist`} className="main_logo flex mr-auto gnb-link">
                        {HAS_CUSTOM_LOGO || hostLogoImg === "onu" ? (
                            <ResponsiveImage smallSrc={BRAND_LOGO} largeSrc={BRAND_LOGO} alt="Service Logo" breakpoint={768} />
                        ) : (
                            <ResponsiveImage smallSrc="/img/dl_symbol.png" largeSrc="/img/dl_logo_white.png" alt="East Commerce Logo" breakpoint={1280} />
                        )}
                    </Link>
                </div>
            </div>

            <div className="flex overflow-hidden">
                <div className="content" id="contents" style={{ marginTop: "0", width: "100%" }}>
                    <div className="payment" style={{ height: "auto" }}>
                        <div className="inner-wrap h-full">
                            <div className="content-wrap h-full" style={{ padding: "0", gap: "20px", justifyContent: "center", display: "block" }}>
                                {/* ---- 결제 완료 카드(캡처 대상) ---- */}
                                <div
                                    ref={receiptRef}
                                    style={{
                                        width: "100%",
                                        maxWidth: 740,
                                        background: "#ffffff",
                                        border: "1px solid #dcdcdc",
                                        borderRadius: 6,
                                        overflow: "hidden",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                        fontFamily:
                                            '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Noto Sans KR",Arial,"Apple SD Gothic Neo",sans-serif',
                                        margin: "0 auto",
                                    }}
                                >
                                    {/* 헤더 */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            background: "#1c6fd9",
                                            padding: "16px",
                                            color: "#fff",
                                            fontWeight: 700,
                                            fontSize: 16,
                                        }}
                                    >
                                        <div>{receipt.title}</div>
                                    </div>

                                    {/* 본문 */}
                                    <div style={{ padding: "16px 16px" }}>
                                        {/* 결제정보 */}
                                        <div style={{ marginTop: 6, marginBottom: 10, fontWeight: 700, color: "#333" }}>결제정보</div>
                                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 14 }}>
                                                <tbody>
                                                    <tr>
                                                        <th style={thStyle}>결제수단</th>
                                                        <td style={tdStyle}>{receipt.method}</td>
                                                    </tr>
                                                    <tr>
                                                        <th style={thStyle}>결제일시</th>
                                                        <td style={tdStyle}>{receipt.date}</td>
                                                    </tr>
                                                    <tr>
                                                        <th style={thStyle}>주문번호</th>
                                                        <td style={tdStyle}>{receipt.tid}</td>
                                                    </tr>
                                                    <tr>
                                                        <th style={thStyle}>결제금액</th>
                                                        <td style={{ ...tdStyle, color: "#d32f2f", fontWeight: 700 }}>{formatKRW(receipt.amount)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* 상점정보 */}
                                        <div style={{ marginTop: 18, marginBottom: 10, fontWeight: 700, color: "#333" }}>상점정보</div>
                                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 14 }}>
                                                <tbody>
                                                    <tr>
                                                        <th style={thStyle}>구매상점명</th>
                                                        <td style={tdStyle}>
                                                            <span style={{ color: "#1c6fd9", fontWeight: 700, cursor: "default" }}>{receipt.storeName}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th style={thStyle}>주문상품명</th>
                                                        <td style={tdStyle}>{receipt.productName}</td>
                                                    </tr>
                                                    <tr>
                                                        <th style={thStyle}>구매자명</th>
                                                        <td style={tdStyle}>{receipt.buyerName}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                {/* ---- /결제 완료 카드 ---- */}

                                {/* 버튼 + 안내 문구 (카드 밖: 캡처 포함 안 됨) */}
                                <div
                                    style={{
                                        marginTop: 14,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 6,
                                        width: "100%",
                                    }}
                                >
                                    <button type="button" onClick={handleDownloadImage} style={primaryBtn}>
                                        이미지로 다운받기
                                    </button>
                                    <div role="note" style={{ textAlign: "center", color: "#6b7280", fontSize: 12, lineHeight: 1.6 }}>
                                        이미지를 다운받아 결제정보를 저장해 주세요.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// 테이블 셀 스타일
const thStyle = {
    width: 100,
    background: "#f7f7f7",
    color: "#333",
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    borderRight: "1px solid #e5e7eb",
    verticalAlign: "top",
    fontWeight: 700,
};
const tdStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#111827",
    wordBreak: "break-all",
};
const primaryBtn = {
    background: "#1c6fd9",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
    minWidth: 160,
};

export default UrlComplete;
